import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { GetUserSubscriptionsResponse, growthSdk } from '@fff-web/growth-sdk';
import GlobalNavInternalContext from '../context/GlobalNavInternalContext';

export type SubscriptionStatus = 'EXPIRED' | 'ACTIVE' | 'CANCELED';

export const useUserSubscriptions = () => {
  const { env } = useContext(GlobalNavInternalContext);

  const growthSdkInstance = useMemo(() => {
    return growthSdk(env.REACT_APP_ENVIRONMENT as Parameters<typeof growthSdk>[0], true);
  }, [env.REACT_APP_ENVIRONMENT]);

  const [clubMembershipSubscription, setClubMembershipSubscription] =
    useState<GetUserSubscriptionsResponse['subscriptions'][number]>();

  const [boxSubscriptions, setBoxSubscriptions] =
    useState<GetUserSubscriptionsResponse['subscriptions'][number][]>();

  const fetchUserSubscriptions = useCallback(async () => {
    const response = await growthSdkInstance.getUserSubscriptions();

    if (!response.ok) return;

    const clubMembershipSub = response.data.subscriptions.find((sub) => sub.type === 'MEMBERSHIP');

    // TODO: refactor properly to handle case for multiple BOX subscriptions
    const allBoxSubs = response.data.subscriptions.filter((sub) => sub.type === 'BOX');
    // Need to select the active one if it exists because a user can have multiple BOX subscriptions with various statuses
    const activeBoxSubs = allBoxSubs.filter((sub) => sub.status === 'ACTIVE');
    const boxSubs = activeBoxSubs?.length
      ? activeBoxSubs
      : allBoxSubs.length ? allBoxSubs : undefined;

    if (!clubMembershipSub && !boxSubs) return;

    if (clubMembershipSub) {
      setClubMembershipSubscription({
        ...clubMembershipSub,
        status: (clubMembershipSub.status === 'NON_RENEWING'
          ? 'CANCELED'
          : clubMembershipSub.status) as SubscriptionStatus,
      });
    }

    if (boxSubs) {
      setBoxSubscriptions(
        boxSubs.map((sub) => ({
          ...sub,
          status: (sub.status === 'NON_RENEWING'
            ? 'CANCELED'
            : sub.status) as SubscriptionStatus,
        }))
      );
    }
  }, [growthSdkInstance]);

  useEffect(() => {
    fetchUserSubscriptions();
  }, [fetchUserSubscriptions]);

  return {
    clubMembershipSubscription,
    boxSubscriptions,
    refetch: fetchUserSubscriptions,
  };
};
